import {useEffect, useState} from 'react';
import type {DesignPageData} from './design-page-selector';
import type {DesignPageSelectorOpts} from './design-page-selector.types';
import {SelectionLimitBehavior} from './design-page-selector.types';
import {fetchPageDataInBulk, fetchMissingPreviews} from './design-page-selector-library';
import {noop} from '@Utils/general.util';

interface UseDesignPageSelectorParams {
  posterId: string;
  pageData?: DesignPageData;
  selectionOpts?: DesignPageSelectorOpts;
}

interface UseDesignPageSelectorResult {
  pageIds: string[];
  pagePreviewUrls: Record<string, string>;
  isLoading: boolean;
  selectedPageIds: Set<string>;
  handlePageClick: (pageId: string) => void;
  isPageSelected: (pageId: string) => boolean;
  isSelectionDisabled: (pageId: string) => boolean;
  isFooterButtonDisabled: () => boolean;
}

export function useDesignPageSelector({posterId, pageData, selectionOpts = {}}: UseDesignPageSelectorParams): UseDesignPageSelectorResult {
  const pageLimit = selectionOpts.pageLimit ?? 1;
  const selectionLimitBehavior = selectionOpts.selectionLimitBehavior ?? SelectionLimitBehavior.PREVENT_SELECTION;
  const onPageLimitExceeded = selectionOpts.onPageLimitExceeded ?? noop;
  const disableRemainingOnPreventSelection = selectionOpts.disableRemainingOnPreventSelection ?? true;
  const enforceAtLeastOneSelection = selectionOpts?.enforceAtLeastOneSelection ?? true;

  const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());
  const [pageIds, setPageIds] = useState<string[]>(pageData?.pageIds ?? []);
  const [pagePreviewUrls, setPagePreviewUrls] = useState<Record<string, string>>(pageData?.previewUrls ?? {});
  const [isLoading, setIsLoading] = useState<boolean>(!pageData);

  useEffect(() => {
    let isMounted = true;

    const loadPageData = async (): Promise<void> => {
      setIsLoading(true);
      const fetchedPageData = await fetchPageDataInBulk(posterId, isMounted);
      if (!fetchedPageData) {
        setIsLoading(false);
        return;
      }

      setPageIds(fetchedPageData.pageIds);
      setPagePreviewUrls(fetchedPageData.previewUrls);
      setIsLoading(false);
    };

    const loadMissingPreviews = async (posterPageData: DesignPageData): Promise<void> => {
      const missingPageIds = posterPageData.pageIds.filter((pageId) => !posterPageData.previewUrls[pageId]);

      if (missingPageIds.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const updatedPreviewUrls = await fetchMissingPreviews(posterId, posterPageData.previewUrls, missingPageIds, isMounted);
      if (updatedPreviewUrls && isMounted) {
        setPagePreviewUrls(updatedPreviewUrls);
        setPageIds(posterPageData.pageIds);
      }
      if (isMounted) {
        setIsLoading(false);
      }
    };

    if (!pageData) {
      void loadPageData();
    } else {
      void loadMissingPreviews(pageData);
    }

    return (): void => {
      isMounted = false;
    };
  }, [posterId, pageData]);

  const handlePageClick = (pageId: string): void => {
    setSelectedPageIds((prev) => {
      const isAlreadySelected = prev.has(pageId);
      const wouldExceedLimit = !isAlreadySelected && pageLimit > 0 && prev.size >= pageLimit;

      if (pageLimit === 1) {
        if (isAlreadySelected) {
          return new Set();
        }
        return new Set([pageId]);
      }

      if (isAlreadySelected) {
        return new Set(Array.from(prev).filter((id) => id !== pageId));
      }

      if (wouldExceedLimit) {
        switch (selectionLimitBehavior) {
          case SelectionLimitBehavior.PREVENT_SELECTION:
            onPageLimitExceeded();
            return prev;

          case SelectionLimitBehavior.DESELECT_FIRST: {
            const prevArray = Array.from(prev);
            return new Set([...prevArray.slice(1), pageId]);
          }

          case SelectionLimitBehavior.ALLOW_OVER_LIMIT: {
            onPageLimitExceeded();
            return new Set([...Array.from(prev), pageId]);
          }

          default:
            return prev;
        }
      }

      return new Set([...Array.from(prev), pageId]);
    });
  };

  const isPageSelected = (pageId: string): boolean => {
    return selectedPageIds.has(pageId);
  };

  const isSelectionDisabled = (pageId: string): boolean => {
    const isSelected = selectedPageIds.has(pageId);
    const isAtSelectionLimit = pageLimit > 0 && selectedPageIds.size >= pageLimit;
    const isPreventSelection = selectionLimitBehavior === SelectionLimitBehavior.PREVENT_SELECTION;
    return disableRemainingOnPreventSelection && isPreventSelection && isAtSelectionLimit && !isSelected;
  };

  const isFooterButtonDisabled = (): boolean => {
    if (selectedPageIds.size === 0 && enforceAtLeastOneSelection) {
      return true;
    }

    if (pageLimit === 1) {
      return selectedPageIds.size === 0;
    }
    return selectedPageIds.size > pageLimit;
  };

  return {
    pageIds,
    pagePreviewUrls,
    isLoading,
    selectedPageIds,
    handlePageClick,
    isPageSelected,
    isSelectionDisabled,
    isFooterButtonDisabled,
  };
}
